import { useTranslation } from 'react-i18next';
import {
  withPortalSectionHidden,
  type PortalHiddenSectionsLike,
  type PortalSection,
} from '@gc/shared';

import { FieldGroup, Section, Toggle } from './controls';
import { PORTAL_SECTION_GROUPS, portalSectionRows } from './portalSections';

/**
 * The sections whose name alone does not say what hiding them does. The rest
 * (Home, Games, Prize Vault) are exactly what they are called.
 *
 * - My session: hiding it takes the balance and points card with it, and the
 *   profile card too — the only place a customer changes their photo. The
 *   owner should also know the clock does NOT go — it moves to the top bar.
 * - Apps: the tab also carries the staff Accounts vault (its first, built-in
 *   tile, and the only way into the vault on a PC). A venue with no apps
 *   enabled sees a near-empty tab and is tempted to tidy it away; the note is
 *   there so the owner knows staff lose the launcher sign-in secrets with it.
 * - Arcade: the owner calls it Challenges; the PCs call it Arcade, and the
 *   name here is the PCs' word so it matches what a customer points at.
 * - Shop: the button is not the only way in, and it is not the kitchen switch.
 * - Call staff (once "Summon a human"; renamed on the PCs 2026-10-17, and
 *   here with them): a paused PC keeps its own, whatever is set here.
 */
const EXPLAINED: ReadonlySet<PortalSection> = new Set<PortalSection>([
  'session',
  'apps',
  'arcade',
  'shop',
  'assist',
]);

/**
 * Settings -> Portal -> "Portal sections" (owner, 2026-09-28): which parts of
 * the signed-in portal the gaming PCs show.
 *
 * A TICK MEANS SHOWN. The column stores what is hidden, so that an empty list
 * is the portal as it always was; the owner, though, is choosing what his
 * customers get, and a list of ticked boxes reads as exactly that.
 *
 * Each name is the portal's own label in the reader's language (the strings
 * test holds them to apps/portal-web's locale files), so "Prize Vault" here is
 * the word on the tab a customer is pointing at.
 *
 * Every flip goes through the shared `withPortalSectionHidden`, which will not
 * hide the last visible tab — and that tab's switch is disabled with the
 * reason beside it, so the page cannot even build the list the API refuses.
 */
export function PortalSectionsCard({
  id,
  hidden,
  onChange,
  error,
}: {
  id?: string;
  hidden: PortalHiddenSectionsLike;
  onChange: (next: PortalSection[]) => void;
  /** A save refusal against `portalHiddenSections`, already translated. */
  error?: string;
}) {
  const { t } = useTranslation();

  function hint(section: PortalSection, locked: boolean): string | undefined {
    const lines = [
      EXPLAINED.has(section) ? t(`settings.general.portalSections.about.${section}`) : null,
      locked ? t('settings.general.portalSections.lastTab') : null,
    ].filter((line): line is string => line !== null);
    return lines.length > 0 ? lines.join(' ') : undefined;
  }

  return (
    <Section id={id} title={t('settings.general.portalSections.title')}>
      <p className="text-sm text-content-secondary">{t('settings.general.portalSections.hint')}</p>

      {PORTAL_SECTION_GROUPS.map((group) => (
        <FieldGroup key={group.id} label={t(`settings.general.portalSections.groups.${group.id}`)}>
          <div className="space-y-2">
            {portalSectionRows(hidden, group.sections).map((row) => (
              <div key={row.section} data-portal-section={row.section}>
                <Toggle
                  label={t(`settings.general.portalSections.names.${row.section}`)}
                  checked={row.shown}
                  disabled={row.locked}
                  hint={hint(row.section, row.locked)}
                  onChange={(show) => onChange(withPortalSectionHidden(hidden, row.section, !show))}
                />
              </div>
            ))}
          </div>
        </FieldGroup>
      ))}

      {error ? (
        <p data-portal-sections-error="" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </Section>
  );
}
